/**
 * Hook for KDS new-order notifications: short beep + flash highlight.
 * Audio context is created lazily on first user click (required by browsers).
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const STORAGE_KEY = 'pekika_kds_sound_enabled';

const playBeep = (audioCtx) => {
  if (!audioCtx) return;
  // Two-tone "ding" — gentle but cuts through noise
  const now = audioCtx.currentTime;
  [
    { freq: 880, start: 0, dur: 0.15 },
    { freq: 1320, start: 0.18, dur: 0.25 },
  ].forEach(({ freq, start, dur }) => {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, now + start);
    gain.gain.linearRampToValueAtTime(0.25, now + start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
    osc.connect(gain).connect(audioCtx.destination);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.05);
  });
};

export const useNewOrderNotifier = (orders) => {
  const audioCtxRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  });
  const [flashIds, setFlashIds] = useState(new Set());

  // Persist sound preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(soundEnabled));
  }, [soundEnabled]);

  // Activate audio context on first user gesture anywhere on the page
  const ensureAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audioCtxRef.current = new Ctx();
    }
    if (audioCtxRef.current?.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  }, []);

  useEffect(() => {
    const handler = () => ensureAudioContext();
    document.addEventListener('click', handler, { once: false });
    return () => document.removeEventListener('click', handler);
  }, [ensureAudioContext]);

  // Detect newly arrived 'new' orders
  useEffect(() => {
    const currentIds = new Set(orders.map(o => o.id));

    if (!initializedRef.current) {
      // Skip notification on first render (don't beep for orders already on screen)
      seenIdsRef.current = currentIds;
      initializedRef.current = true;
      return;
    }

    const fresh = orders.filter(
      o => o.kitchen_status === 'new' && !seenIdsRef.current.has(o.id)
    );

    if (fresh.length > 0) {
      if (soundEnabled) playBeep(audioCtxRef.current);
      const ids = new Set(fresh.map(o => o.id));
      setFlashIds(prev => new Set([...prev, ...ids]));
      // Clear flash after 6s
      setTimeout(() => {
        setFlashIds(prev => {
          const next = new Set(prev);
          ids.forEach(id => next.delete(id));
          return next;
        });
      }, 6000);
    }

    seenIdsRef.current = currentIds;
  }, [orders, soundEnabled]);

  const toggleSound = useCallback(() => {
    ensureAudioContext();
    setSoundEnabled(s => !s);
  }, [ensureAudioContext]);

  // Quick "test" sound for the toggle button
  const testSound = useCallback(() => {
    ensureAudioContext();
    playBeep(audioCtxRef.current);
  }, [ensureAudioContext]);

  return { soundEnabled, toggleSound, testSound, flashIds };
};
